import { getDependencyValueAsync } from "Dependency2";
import type DependencyExpression from "DependencyExpression";
import type DependencyGraph from "DependencyGraph";
import { getDependencyGraph } from "EntityDependencyExtensions";
import entityExpander from "EntityExpander";
import PropertyVertex from "PropertyVertex";
import type { CacheDependencyFunc, default as Rule } from "Rule";
import { State } from "StateConstants";
import type { Entity } from "breeze-client";
import ko, { type Subscription } from "knockout";
import CalculatedPropertyVertex from "./CalculatedPropertyVertex.ts";
import type { DependencyStrategyContext } from "./DependencyStrategyContext";

export default class DependencyGraphStrategy {
  private calculatedPropertyVertex: CalculatedPropertyVertex | undefined;
  private readonly context: DependencyStrategyContext;
  private disposed: boolean;
  private initialLoader: Promise<void> | undefined;
  private initiallyLoaded: boolean;
  private observableSubscription: Subscription | undefined;

  constructor(context: DependencyStrategyContext) {
    this.calculatedPropertyVertex = undefined;
    this.context = context;
    this.disposed = false;
    this.initialLoader = undefined;
    this.initiallyLoaded = false;
    this.observableSubscription = undefined;
  }

  dispose(): void {
    this.disposed = true;
    this.calculatedPropertyVertex?.dispose();
    this.observableSubscription?.dispose();
  }

  handleValueChanged(newValue: unknown): void {
    if (this.observableSubscription) {
      this.observableSubscription.dispose();
      this.observableSubscription = undefined;
    }
    if (ko.isObservable(newValue)) {
      this.observableSubscription = newValue.subscribe(() => {
        if (this.context.getState() !== State.NotLoaded) {
          this.notifyDependentsAsync(false);
        }
      });
    }
  }

  async notifyDependentsAsync(loadedOnly: boolean): Promise<void> {
    const { context } = this;
    const { property } = context.rule;
    if (property && this.canUpdateDependencies()) {
      await this.dependencyGraph.notifyDependentsAsync(new PropertyVertex(context.entity, property), loadedOnly);
    }
  }

  performInitialLoad(): Promise<void> | void {
    if (this.initialLoader) {
      return this.initialLoader;
    }

    if (!this.canUpdateDependencies()) {
      return;
    }

    if (this.initiallyLoaded) {
      return;
    }

    this.initialLoader = this.performInitialLoadAsync();
    return this.initialLoader;
  }

  private get dependencyGraph(): DependencyGraph {
    return getDependencyGraph(this.context.entity.entityAspect.entityManager);
  }

  private canUpdateDependencies(): boolean {
    const { entityState } = this.context.entity.entityAspect;
    return !this.disposed && !entityState.isDeleted() && !entityState.isDetached();
  }

  private initGraph(cacheDependencyFunc: CacheDependencyFunc | undefined): void {
    const { context } = this;
    const vertex = new CalculatedPropertyVertex(context, cacheDependencyFunc);
    if (context.rule.property) {
      this.dependencyGraph.addOrReplaceDependencies(new PropertyVertex(context.entity, context.rule.property), [
        vertex,
      ]);
    }
    vertex.wireDependencies();
    this.calculatedPropertyVertex = vertex;
  }

  private async performInitialLoadAsync(): Promise<void> {
    const { context } = this;
    const { cacheDependencyFunc, dependencies } = await loadDependenciesAsync(context.entity, context.rule);
    this.initiallyLoaded = true;
    this.initialLoader = undefined;

    if (this.canUpdateDependencies() && (cacheDependencyFunc || dependencies.length)) {
      this.initGraph(cacheDependencyFunc);
    }
  }
}

async function loadDependenciesAsync(
  entity: Entity,
  rule: Rule,
): Promise<{ cacheDependencyFunc: CacheDependencyFunc | undefined; dependencies: DependencyExpression[] }> {
  const gettingCacheDependencyFunc = rule.getCacheDependencyFuncAsync();

  await entityExpander.expandPathsAsync(entity, rule.expandPaths);
  const dependencies = await rule.getDependenciesAsync(entity);
  await Promise.all(dependencies.map((d) => getDependencyValueAsync(entity, d.expression)));

  const cacheDependencyFunc = await gettingCacheDependencyFunc;
  return { cacheDependencyFunc, dependencies };
}
